import type { Alert } from '@tunasong/models'
import { useAlert } from '@tunasong/ui-lib'
import type { RefObject } from 'react'
import { useEffect } from 'react'
import { useGlobalPlayerState } from './global-player-state.hook.js'

/** `MediaError.MEDIA_ERR_DECODE`, as a literal so it reads the same without a DOM global */
const mediaErrDecode = 3

/**
 * The browser cannot decode the file (GH #2008: Chrome and Edge reject some valid MP3s partway through). Only
 * MEDIA_ERR_DECODE counts. MEDIA_ERR_SRC_NOT_SUPPORTED is also what an expired signed URL or a 404 raises, and
 * advice to re-export the file would be wrong there; a retry fixes those.
 */
export const isDecodeError = (el: HTMLMediaElement | null | undefined) => el?.error?.code === mediaErrDecode

/** Joni's copy: one text for the owner and a recipient alike */
export const decodeErrorAlert = (name: string | undefined): Alert => ({
  severity: 'error',
  title: name ? `Can't play "${name}" in this browser` : "Can't play this recording in this browser",
  message:
    "This browser can't decode the file. It may play in Safari or Firefox. Re-exporting it as AAC or MP3 and uploading it again fixes it for everyone.",
})

/** Alert once per file per session, not on every seek or replay */
const alertedMediaIds = new Set<string>()

/** For tests: forget which files have been alerted */
export const resetDecodeErrorAlerts = () => alertedMediaIds.clear()

/**
 * Say so when the global player stops on a file the browser cannot decode; it used to stop without a word. Left to
 * a source that shows its own errors inline, such as a playlist.
 */
export const useDecodeErrorAlert = (audioRef: RefObject<HTMLAudioElement | null>) => {
  const { alert } = useAlert()

  useEffect(() => {
    const el = audioRef.current
    if (!el) {
      return
    }
    const handleError = () => {
      if (!isDecodeError(el)) {
        return
      }
      const { active, inlineErrorSources } = useGlobalPlayerState.getState()
      const mediaId = active?.media.id
      if (!mediaId || alertedMediaIds.has(mediaId)) {
        return
      }
      if (active.sourceEntityId && inlineErrorSources.includes(active.sourceEntityId)) {
        return
      }
      alertedMediaIds.add(mediaId)
      alert(decodeErrorAlert(active.media.name))
    }
    el.addEventListener('error', handleError)
    return () => el.removeEventListener('error', handleError)
  }, [audioRef, alert])
}

/** A source that shows its tracks' errors inline registers here while mounted, so the global alert stays quiet */
export const useInlineMediaErrors = (sourceEntityId: string | undefined) => {
  const addInlineErrorSource = useGlobalPlayerState(state => state.addInlineErrorSource)
  const removeInlineErrorSource = useGlobalPlayerState(state => state.removeInlineErrorSource)

  useEffect(() => {
    if (!sourceEntityId) {
      return
    }
    addInlineErrorSource(sourceEntityId)
    return () => removeInlineErrorSource(sourceEntityId)
  }, [sourceEntityId, addInlineErrorSource, removeInlineErrorSource])
}
